import { lazy, useEffect, useState } from "react";
import { useParams } from "wouter";
import { getPostBySlug } from "@/data/blog";
import { fetchProductByHandle, getCachedProduct } from "@/lib/shopifyApi";
import { PageBreadcrumb } from "@/components/sections/PageBreadcrumb";

const ProductPage = lazy(() => import("./product"));
const BlogArtykulPage = lazy(() => import("./blog-artykul"));
const NotFound = lazy(() => import("./not-found"));

type Resolution =
  | { status: "checking" }
  | { status: "product" }
  | { status: "article" }
  | { status: "not-found" }
  | { status: "error"; message: string };

/**
 * Root-level resolver. Shopify products own a slug whenever both the
 * catalog and the editorial data contain it. An API failure is not treated
 * as a product miss, so a transient outage cannot silently show an article
 * at a product URL.
 */
export default function RootContentPage() {
  const { handle = "" } = useParams<{ handle: string }>();
  const slug = handle.replace(/^\/+|\/+$/g, "");
  const post = getPostBySlug(slug);
  const [resolution, setResolution] = useState<Resolution>(() => {
    const cachedProduct = getCachedProduct(slug);
    if (cachedProduct) return { status: "product" };
    // The article is static and is already present in a prerendered snapshot.
    // Keep this state during hydration while checking product precedence in
    // the background, otherwise hydration erases the SEO HTML.
    if (post) return { status: "article" };
    return { status: "checking" };
  });

  useEffect(() => {
    let active = true;
    const cached = getCachedProduct(slug);
    if (cached) {
      setResolution({ status: "product" });
      return () => { active = false; };
    }

    // Blog slugs are validated against the Shopify catalog when sitemap URLs
    // are built. Avoid an expected product 404 for every article navigation.
    if (post) {
      setResolution({ status: "article" });
      return () => { active = false; };
    }

    setResolution({ status: "checking" });
    fetchProductByHandle(slug)
      .then(product => {
        if (!active) return;
        if (product) {
          setResolution({ status: "product" });
        } else {
          setResolution({ status: "not-found" });
        }
      })
      .catch(error => {
        if (!active) return;
        console.error(`[root-content-resolver] Could not resolve "${slug}"`, error);
        setResolution({
          status: "error",
          message: "Nie można teraz sprawdzić tego adresu. Spróbuj ponownie za chwilę.",
        });
      });

    return () => { active = false; };
  }, [slug, post]);

  if (resolution.status === "checking") {
    return <div aria-label="Ładowanie" className="min-h-[100dvh] bg-background" />;
  }
  if (resolution.status === "product") {
    return <ProductPage key={slug} />;
  }
  if (resolution.status === "article") {
    return <BlogArtykulPage key={slug} />;
  }
  if (resolution.status === "not-found") {
    return <NotFound />;
  }

  return (
    <div className="min-h-[60vh] flex items-center justify-center px-6 text-center">
      <PageBreadcrumb crumbs={[{ label: 'Nie można wczytać strony' }]} />
      <p className="max-w-md text-muted-foreground">{resolution.message}</p>
    </div>
  );
}
